// src/pages/ClientsPage.js
import React, { useEffect, useState, useMemo } from "react";
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  Timestamp,
  query,
  where
} from "firebase/firestore";
import { db, callXeroFunction } from "../firebase.js";
import { useAuth }                    from "../context/AuthContext.js";
import { useLanguage }                from "../context/LanguageContext.js";
import { t }                          from "../utils/translations.js";
import {
  Container,
  Card,
  Table,
  Button,
  Form,
  Row,
  Col,
  Modal
} from "react-bootstrap";
import LoadingSpinner from "../components/common/LoadingSpinner.js";
import { useGoogleMaps } from "../hooks/useGoogleMaps.js";
import { useClientForm } from "../hooks/useClientForm.js";
import ClientFormFields from "../components/ClientFormFields.js";
import "./ClientsPage.css";

export default function ClientsPage() {
  const { user } = useAuth();
  const { language } = useLanguage();

  // Load Google Maps Places library
  const { addressService, nzBounds } = useGoogleMaps();

  // Hook unificado para formulario ADD (substitui 11 estados newX)
  const addForm = useClientForm(language);

  // Hook unificado para formulario EDIT (substitui 11 estados editX)
  const editForm = useClientForm(language);

  // mobile + UI states
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // client list state
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  // delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [clientJobs, setClientJobs] = useState([]);

  // load clients from PostgreSQL via backend API
  useEffect(() => {
    (async () => {
      try {
        const response = await callXeroFunction('getClients', {});
        setClients(response.clients || []);
      } catch (error) {
        console.error('Erro ao carregar clientes:', error);
        alert('Erro ao carregar clientes do banco de dados');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Handle window resize for responsive view
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filtro de busca por nome, endereço ou telefone
  const filteredClients = useMemo(() => {
    const sorted = [...clients].sort((a, b) => a.name.localeCompare(b.name));
    if (!searchQuery.trim()) return sorted;
    const q = searchQuery.toLowerCase();
    return sorted.filter(c =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.address || '').toLowerCase().includes(q) ||
      (c.phone || '').toLowerCase().includes(q)
    );
  }, [clients, searchQuery]);

  // block non-admins e non-client-managers
  const isAuthorized = user?.isAdmin || user?.isClientManager;
  if (!isAuthorized) {
    return (
      <Container className="py-4">
        <h5>{t(language, 'accessDenied')}</h5>
      </Container>
    );
  }

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  // Log de alterações (apenas para client managers)
  const logClientChange = async (action, clientName, details) => {
    if (!user?.isClientManager) return; // só registra se for client manager

    try {
      await addDoc(collection(db, "clientChangeLogs"), {
        action: action, // "add" ou "edit"
        clientName: clientName,
        managerId: user.uid,
        managerName: user.displayName || user.email,
        details: details,
        timestamp: Timestamp.now()
      });

      // Envia notificação via webhook para o Zapier
      const detailsText = Object.entries(details)
        .map(([key, value]) => `${key}: ${value}`)
        .join(" | ");

      const webhookUrl = new URL("https://hooks.zapier.com/hooks/catch/25065063/ur575h7/");
      webhookUrl.searchParams.append("manager_name", user.displayName || user.email);
      webhookUrl.searchParams.append("client_name", clientName);
      webhookUrl.searchParams.append("action_type", action === "add" ? "Adicionado" : "Editado");
      webhookUrl.searchParams.append("changes", detailsText);
      webhookUrl.searchParams.append("timestamp", new Date().toLocaleString("pt-BR"));

      await fetch(webhookUrl.toString(), {
        method: "POST"
      }).catch(err => console.error("Erro ao enviar webhook:", err));
    } catch (err) {
      console.error("Erro ao registrar log:", err);
    }
  };

  // handler to add a new client (usando hook unificado addForm)
  const handleAdd = async e => {
    e.preventDefault();

    // Validacao usando o hook
    const validation = addForm.validate();
    if (!validation.isValid) {
      return alert(validation.error);
    }

    try {
      // Obtem dados formatados para API (snake_case, normaliza endereco, etc)
      const clientData = addForm.getApiData();

      // Create via REST API
      const result = await callXeroFunction('createClient', clientData, { method: 'POST' });

      // Registra log se for client manager
      await logClientChange("add", addForm.formData.name.trim(), {
        address: clientData.address,
        hours: addForm.formData.hours,
        email: addForm.formData.email.trim(),
        phone: addForm.formData.phone.trim(),
        defaultAmount: addForm.formData.defaultAmount,
        paymentMethod: addForm.formData.paymentMethod
      });

      // Add to local state with the returned client (includes ID)
      setClients([
        {
          ...result.client,
          default_checklist: addForm.formData.checklist,
          restricted_times: addForm.formData.restrictedTimes
        },
        ...clients
      ]);

      // Reset form usando o hook
      addForm.closeForm();
      addForm.openAddMode();
      if (isMobile) setShowAddForm(false);
    } catch (err) {
      console.error(err);
      alert(t(language, 'errorAddingClient'));
    }
  };

  // open edit modal (usando hook unificado editForm)
  const openEdit = c => {
    editForm.openEditMode(c);
  };

  // save edits (usando hook unificado editForm)
  const handleSaveEdit = async () => {
    // Validacao usando o hook
    const validation = editForm.validate();
    if (!validation.isValid) {
      return alert(validation.error);
    }

    editForm.setSaving(true);
    try {
      // Obtem dados formatados para API (snake_case, normaliza endereco, etc)
      const updatedData = editForm.getApiData();

      // Obtem mudancas para log (ja inclui deteccao de aumento de horas)
      const changes = editForm.getChanges();

      // Update via REST API
      await callXeroFunction('updateClient', updatedData, { method: 'PUT', id: editForm.activeClientId });

      // Registra log se houve mudancas
      if (Object.keys(changes).length > 0) {
        await logClientChange("edit", editForm.formData.name.trim(), changes);
      }

      // Update local state with new data
      setClients(clients.map(c =>
        c.id === editForm.activeClientId
          ? {
              ...c,
              ...updatedData,
              default_checklist: editForm.formData.checklist,
              restricted_times: editForm.formData.restrictedTimes
            }
          : c
      ));

      // Fecha modal e reseta form
      editForm.closeForm();
    } catch (err) {
      console.error(err);
      alert(t(language, 'errorSavingClient'));
    }
    editForm.setSaving(false);
  };

  // delete client - check for associated jobs first
  const handleDelete = async id => {
    if (!window.confirm(t(language, 'confirmDeleteClient'))) return;

    try {
      // Check if client has any jobs (events) associated
      const jobsQuery = query(collection(db, "jobs"), where("clientId", "==", String(id)));
      const jobsSnapshot = await getDocs(jobsQuery);
      const jobs = jobsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      if (jobs.length > 0) {
        // Get today's date to separate past and future jobs
        const today = new Date().toISOString().split('T')[0];
        const futureJobs = jobs.filter(j => j.date >= today);

        // Show modal with options
        setClientToDelete(id);
        setClientJobs({
          all: jobs,
          future: futureJobs,
          past: jobs.filter(j => j.date < today)
        });
        setShowDeleteModal(true);
      } else {
        // No jobs, delete the client via API
        await callXeroFunction('deleteClient', {}, { method: 'DELETE', id });
        setClients(clients.filter(c => c.id !== id));
      }
    } catch (err) {
      console.error(err);
      alert(t(language, 'errorDeletingClient'));
    }
  };

  // Delete client with selected jobs option
  const confirmDeleteClient = async (deleteOption) => {
    if (!clientToDelete) return;

    try {
      // Delete jobs based on option
      let jobsToDelete = [];

      if (deleteOption === "all") {
        jobsToDelete = clientJobs.all;
      } else if (deleteOption === "future") {
        jobsToDelete = clientJobs.future;
      }
      // If "none", don't delete any jobs

      // Delete selected jobs
      for (const job of jobsToDelete) {
        await deleteDoc(doc(db, "jobs", job.id));
      }

      // Delete the client via API
      await callXeroFunction('deleteClient', {}, { method: 'DELETE', id: clientToDelete });
      setClients(clients.filter(c => c.id !== clientToDelete));

      // Close modal and reset state
      setShowDeleteModal(false);
      setClientToDelete(null);
      setClientJobs([]);

      alert(language === "en"
        ? `Client deleted successfully${jobsToDelete.length > 0 ? ` along with ${jobsToDelete.length} job(s)` : ''}`
        : `Cliente deletado com sucesso${jobsToDelete.length > 0 ? ` junto com ${jobsToDelete.length} job(s)` : ''}`);
    } catch (err) {
      console.error(err);
      alert(t(language, 'errorDeletingClient'));
    }
  };

  return (
    <Container className={`py-4 clients-page ${isMobile ? 'clients-mobile' : ''}`}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">{t(language, 'manageClients')}</h3>
        {!isMobile && (
          <Button
            variant={showAddForm ? "outline-secondary" : "primary"}
            onClick={() => setShowAddForm(!showAddForm)}
          >
            {showAddForm ? (language === 'en' ? 'Close' : 'Fechar') : (language === 'en' ? '+ New Client' : '+ Novo Cliente')}
          </Button>
        )}
      </div>

      {/* Add form - collapsible */}
      {showAddForm && (
        <Card className="mb-3 p-3 clients-add-form-card">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <strong>{language === 'en' ? 'New Client' : 'Novo Cliente'}</strong>
            <Button variant="link" className="p-0 text-muted" onClick={() => setShowAddForm(false)}>
              ✕
            </Button>
          </div>
          <Form onSubmit={handleAdd}>
            <ClientFormFields
              formData={addForm.formData}
              onFieldChange={addForm.updateField}
              onNestedFieldChange={addForm.updateNestedField}
              isAdmin={user?.isAdmin}
              addressService={addressService}
              nzBounds={nzBounds}
              language={language}
              compact={false}
              showRestrictedTimes={false}
              showNotes={false}
            />
            <Row className="g-2 mt-2">
              <Col md={12}>
                <Button type="submit" className="w-100">{t(language, 'addClient')}</Button>
              </Col>
            </Row>
          </Form>
        </Card>
      )}

      {/* Search bar */}
      <div className="clients-search-bar mb-3">
        <Form.Control
          type="text"
          placeholder={language === 'en' ? 'Search by name, address or phone...' : 'Buscar por nome, endereco ou telefone...'}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="clients-search-input"
        />
        {searchQuery && (
          <button className="clients-search-clear" onClick={() => setSearchQuery("")} type="button">
            ✕
          </button>
        )}
      </div>

      {/* Counter */}
      <small className="text-muted d-block mb-2">
        {filteredClients.length} {language === 'en' ? 'clients' : 'clientes'}
        {searchQuery && ` (${language === 'en' ? 'filtered' : 'filtrados'})`}
      </small>

      {/* Desktop: Table */}
      {!isMobile && (
        <Table hover responsive>
          <thead>
            <tr>
              <th>{t(language, 'clientName')}</th>
              <th>{t(language, 'address')}</th>
              <th>{t(language, 'hours')}</th>
              <th>Email</th>
              <th>Telefone</th>
              {user?.isAdmin && <th>Valor</th>}
              {user?.isAdmin && <th>Pagamento</th>}
              <th>{t(language, 'actions')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredClients.map(c => (
              <tr key={c.id}>
                <td>{c.name}</td>
                <td>{c.address}</td>
                <td>{c.service_duration || c.hours || "-"}</td>
                <td>{c.email || "-"}</td>
                <td>{c.phone || "-"}</td>
                {user?.isAdmin && <td>${parseFloat(c.default_price || c.defaultInvoiceAmount || 0).toFixed(2)}</td>}
                {user?.isAdmin && (
                  <td>
                    <span className={`badge ${(c.payment_method || c.paymentMethod) === 'xero' ? 'bg-info' : 'bg-secondary'}`}>
                      {(c.payment_method || c.paymentMethod) === 'xero' ? 'Xero' : 'Dinheiro'}
                    </span>
                  </td>
                )}
                <td>
                  <Button size="sm" variant="outline-primary" onClick={() => openEdit(c)}>
                    {t(language, 'edit')}
                  </Button>{" "}
                  {user?.isAdmin && (
                    <Button size="sm" variant="outline-danger" onClick={() => handleDelete(c.id)}>
                      {t(language, 'delete')}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Mobile: Cards */}
      {isMobile && (
        <div className="clients-card-list">
          {filteredClients.map(c => {
            const payMethod = c.payment_method || c.paymentMethod;
            return (
              <div key={c.id} className="clients-card">
                <div className="clients-card-header">
                  <span className="clients-card-name">{c.name}</span>
                  {user?.isAdmin && (
                    <span className={`badge ${payMethod === 'xero' ? 'bg-info' : 'bg-secondary'}`}>
                      {payMethod === 'xero' ? 'Xero' : 'Cash'}
                    </span>
                  )}
                </div>
                {c.address && (
                  <div className="clients-card-detail">
                    <span className="clients-card-icon">📍</span> {c.address}
                  </div>
                )}
                <div className="clients-card-row">
                  {(c.service_duration || c.hours) && (
                    <span className="clients-card-detail">
                      <span className="clients-card-icon">🕐</span> {c.service_duration || c.hours}h
                    </span>
                  )}
                  {c.phone && (
                    <span className="clients-card-detail">
                      <span className="clients-card-icon">📞</span> {c.phone}
                    </span>
                  )}
                  {user?.isAdmin && (
                    <span className="clients-card-detail">
                      <strong>${parseFloat(c.default_price || c.defaultInvoiceAmount || 0).toFixed(2)}</strong>
                    </span>
                  )}
                </div>
                <div className="clients-card-actions">
                  <button className="clients-action-btn clients-action-edit" onClick={() => openEdit(c)} title={t(language, 'edit')}>
                    ✏️
                  </button>
                  {user?.isAdmin && (
                    <button className="clients-action-btn clients-action-delete" onClick={() => handleDelete(c.id)} title={t(language, 'delete')}>
                      🗑️
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* FAB - Mobile only */}
      {isMobile && !showAddForm && (
        <button
          className="clients-fab"
          onClick={() => setShowAddForm(true)}
          title={language === 'en' ? 'Add Client' : 'Adicionar Cliente'}
        >
          +
        </button>
      )}

      {/* Edit modal - usando componentes refatorados */}
      <Modal show={editForm.isEditMode} onHide={() => editForm.closeForm()} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{t(language, 'editClient')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <ClientFormFields
              formData={editForm.formData}
              onFieldChange={editForm.updateField}
              onNestedFieldChange={editForm.updateNestedField}
              isAdmin={user?.isAdmin}
              addressService={addressService}
              nzBounds={nzBounds}
              language={language}
              compact={true}
              showRestrictedTimes={true}
              showNotes={true}
              showChecklist={true}
              onAddChecklistItem={editForm.addChecklistItem}
              onRemoveChecklistItem={editForm.removeChecklistItem}
            />
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => editForm.closeForm()}>
            {t(language, 'cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveEdit}
            disabled={editForm.saving}
          >
            {editForm.saving ? t(language, 'saving') : t(language, 'save')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Client Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            🗑️ {language === "en" ? "Delete Client with Jobs" : "Deletar Cliente com Jobs"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            {language === "en"
              ? `This client has ${clientJobs.all?.length || 0} associated job(s). What would you like to do with them?`
              : `Este cliente tem ${clientJobs.all?.length || 0} job(s) associado(s). O que você gostaria de fazer com eles?`}
          </p>

          {clientJobs.past?.length > 0 && (
            <div className="mb-3 p-2 bg-light rounded">
              <small>
                <strong>{language === "en" ? "Past jobs:" : "Jobs passados:"}</strong> {clientJobs.past.length}
              </small>
            </div>
          )}

          {clientJobs.future?.length > 0 && (
            <div className="mb-3 p-2 bg-light rounded">
              <small>
                <strong>{language === "en" ? "Future jobs:" : "Jobs futuros:"}</strong> {clientJobs.future.length}
              </small>
            </div>
          )}

          <div className="d-grid gap-2">
            <Button
              variant="outline-danger"
              onClick={() => confirmDeleteClient("all")}
              className="text-start"
            >
              <strong>{language === "en" ? "Delete all jobs" : "Deletar todos os jobs"}</strong>
              <br />
              <small className="text-muted">
                {language === "en"
                  ? "Delete the client and ALL associated jobs (past and future)"
                  : "Deletar o cliente e TODOS os jobs associados (passados e futuros)"}
              </small>
            </Button>

            {clientJobs.future?.length > 0 && (
              <Button
                variant="outline-warning"
                onClick={() => confirmDeleteClient("future")}
                className="text-start"
              >
                <strong>{language === "en" ? "Delete future jobs only" : "Deletar apenas jobs futuros"}</strong>
                <br />
                <small className="text-muted">
                  {language === "en"
                    ? `Delete the client and ${clientJobs.future.length} future job(s), keep past jobs`
                    : `Deletar o cliente e ${clientJobs.future.length} job(s) futuro(s), manter jobs passados`}
                </small>
              </Button>
            )}

            <Button
              variant="outline-primary"
              onClick={() => confirmDeleteClient("none")}
              className="text-start"
            >
              <strong>{language === "en" ? "Keep all jobs" : "Manter todos os jobs"}</strong>
              <br />
              <small className="text-muted">
                {language === "en"
                  ? "Delete only the client, keep all jobs"
                  : "Deletar apenas o cliente, manter todos os jobs"}
              </small>
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowDeleteModal(false);
            setClientToDelete(null);
            setClientJobs([]);
          }}>
            {language === "en" ? "Cancel" : "Cancelar"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
